import { TRPCLink } from '@trpc/client';
import { createVersionHeaderLink } from './versionHeaderLink';
import type { AnyTRPCRouter } from '@trpc/server';

export interface VersionCheckConfig {
  onVersionMismatch?: (clientVersion: string, serverVersion: string) => void;
  onVersionDetected?: (serverVersion: string) => void;
  clientVersion: string;
}

/**
 * Creates a TRPC link with integrated version checking capabilities.
 * This is a higher-level utility that combines version header detection with version checking logic.
 *
 * @param config - Version checking configuration
 * @returns TRPC link that handles version checking automatically
 */
export function createVersionCheckingLink<TRouter extends AnyTRPCRouter>(
  config: VersionCheckConfig,
): TRPCLink<TRouter> {
  let hasNotified = false;

  const onVersionDetected = (serverVersion: string) => {
    const currentClientVersion = config.clientVersion;

    config.onVersionDetected?.(serverVersion);

    const isMismatch =
      serverVersion &&
      currentClientVersion &&
      serverVersion !== currentClientVersion &&
      serverVersion !== 'local' &&
      currentClientVersion !== 'local';

    if (isMismatch == true && !hasNotified && config.onVersionMismatch) {
      hasNotified = true;
      config.onVersionMismatch(currentClientVersion, serverVersion);
    }
  };

  return createVersionHeaderLink<TRouter>(onVersionDetected);
}
